import { PasswordInput } from '../ui/password-input'
import { PasswordRequirements } from '../ui/password-requirements'
import { Alert, AlertDescription } from '../ui/alert'
import { Button } from '../ui/button'
import { Label } from '@/components/ui/label'

interface SetupPasswordProps {
  /** Current password value; drives the live requirements checklist. */
  password: string
  setPassword: (password: string) => void
  setConfirmPassword: (confirmPassword: string) => void
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void
  isLoading?: boolean
  formError?: string
}

export function SetupPassword({
  password,
  setPassword,
  setConfirmPassword,
  handleSubmit,
  isLoading,
  formError,
}: SetupPasswordProps) {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
        <div className="flex min-h-[500px]">
          {/* Left Panel - Marketing Content */}
          <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden rounded-l-2xl">
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(/fanfairpartnerlogin.jpg)` }}
            />
            {/* Header */}
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between z-10">
              <div className="flex items-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8" />
              </div>
            </div>
            {/* Content */}
            <div className="relative z-10 flex items-center justify-center p-12">
              <div className="text-center text-white max-w-md">
                <h1 className="text-4xl font-bold mb-4 text-white">Capturing Moments, Creating Memories</h1>
                <p className="text-lg opacity-90 mb-8 text-white">
                  Welcome to FanFair. Set your password to activate your account.
                </p>
                {/* Slide indicators */}
                <div className="flex justify-center space-x-2">
                  <div className="w-8 h-1 bg-white rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
          {/* Right Panel - Registration Form */}
          <div className="w-full lg:w-1/2 bg-slate-800 flex items-center justify-center p-6 lg:p-12">
            <div className="w-full max-w-sm space-y-8">
              {/* Mobile Header */}
              <div className="lg:hidden text-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8 mx-auto" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-medium text-white">Complete Your Registration</h1>
                <p className="text-slate-400 font-light">Set a password to activate your account.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                {formError && (
                  <Alert variant="destructive" className="mt-4">
                    <AlertDescription className="text-white">{formError}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password" className="text-sm font-light text-white">
                      New Password
                    </Label>
                    <PasswordInput
                      id="new-password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      data-testid="password-input"
                      className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-primary focus:ring-primary/20 pr-10"
                    />
                    <PasswordRequirements password={password} className="text-slate-400" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password" className="text-sm font-light text-white">
                      Confirm Password
                    </Label>
                    <PasswordInput
                      id="confirm-password"
                      placeholder="Confirm your password"
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      data-testid="confirm-password-input"
                      className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-primary focus:ring-primary/20 pr-10"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {isLoading ? 'Setting Password...' : 'Set Password'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
